import { subscriptionStatusService } from '../subscriptionStatusService';
import trialService from '../trialService';
import { getAuth } from 'firebase/auth';
import {
  featureAvailable,
  getPlanLimits,
  resolvePlanFromPriceId,
  type PlanFeatureKey,
  type PlanTier,
} from '../../config/planLimits';

// ===== INTERFACES (compatível com callers existentes) =====

export interface PlanLimits {
  patients: number | 'unlimited';
  professionals: number | 'unlimited';
  telemedicineSessions: number | 'unlimited';
  storageGB: number | 'unlimited';
  reportsPerMonth: number | 'unlimited';
  aiAnalyses: number | 'unlimited';
  pharmacyIntegration: boolean;
  laboratoryIntegration: boolean;
  apiAccess: boolean;
  customFields: number | 'unlimited';
  backupFrequency: 'daily' | 'weekly' | 'monthly';
  supportLevel: 'email' | 'priority' | '24/7';
  managerDedicated: boolean;
  hasAI: boolean;
  hasHomeCare: boolean;
  hasTiss: boolean;
  hasSngpc: boolean;
  hasInjectables: boolean;
  hasRecurringBilling: boolean;
  hasAdvancedReports: boolean;
  hasFinancialModule: boolean;
}

export interface CurrentUsage {
  patients: number;
  professionals: number;
  telemedicineSessions: number;
  storageUsedGB: number;
  reportsThisMonth: number;
  aiAnalysesThisMonth: number;
}

export interface LimitCheckResult {
  allowed: boolean;
  currentUsage: number;
  limit: number | 'unlimited';
  remaining?: number | 'unlimited';
  message?: string;
  upgradeRequired?: boolean;
}

function toServiceLimits(tier: PlanTier): PlanLimits {
  const c = getPlanLimits(tier);
  const num = (n: number): number | 'unlimited' => (Number.isFinite(n) ? n : 'unlimited');
  return {
    patients: num(c.maxPatients),
    professionals: num(c.maxProfessionals),
    telemedicineSessions: num(c.maxTelemedicinePerMonth),
    storageGB: num(c.maxStorageGB),
    reportsPerMonth: c.hasAdvancedReports ? 'unlimited' : 10,
    aiAnalyses: num(c.maxAiAnalysesPerMonth),
    pharmacyIntegration: c.hasPharmacy,
    laboratoryIntegration: c.hasLaboratory,
    apiAccess: c.hasApiAccess,
    customFields: c.isUnlimited
      ? 'unlimited'
      : c.planId === 'professional' || c.planId === 'solo' || c.planId === 'plus'
        ? 20
        : 5,
    backupFrequency: c.planId === 'starter' || c.planId === 'trial' ? 'weekly' : 'daily',
    supportLevel:
      c.supportLevel === '24/7+gestor' || c.supportLevel === '24/7'
        ? '24/7'
        : c.supportLevel === 'priority'
          ? 'priority'
          : 'email',
    managerDedicated: c.supportLevel === '24/7+gestor',
    hasAI: c.hasAI,
    hasHomeCare: c.hasHomeCare,
    hasTiss: c.hasTiss,
    hasSngpc: c.hasSngpc,
    hasInjectables: c.hasInjectables,
    hasRecurringBilling: c.hasRecurringBilling,
    hasAdvancedReports: c.hasAdvancedReports,
    hasFinancialModule: c.hasFinancialModule,
  };
}

class PlanLimitsService {
  private auth = getAuth();

  async getCurrentPlan(): Promise<PlanTier> {
    try {
      const user = this.auth.currentUser;
      if (!user) return 'trial';

      // Admin / demo → enterprise
      if (user.email?.endsWith('@upclinic.com') || user.email === 'demo@upclinic.com') {
        return 'enterprise';
      }

      const subscriptionStatus = await subscriptionStatusService.checkSubscriptionStatus(user.uid);

      // Assinatura Stripe ativa (inclui Stripe trialing) → mapear Price ID
      if (subscriptionStatus.hasActiveSubscription) {
        const priceId =
          subscriptionStatus.subscription?.price?.id ||
          (subscriptionStatus.subscription as { price_id?: string } | undefined)?.price_id ||
          '';
        const priceMetadataPlan =
          subscriptionStatus.subscription?.price?.metadata?.plan ||
          subscriptionStatus.subscription?.metadata?.plan ||
          null;

        const plan = resolvePlanFromPriceId(priceId || null, {
          hasActivePaidSubscription: true,
          priceMetadataPlan,
        });
        console.log('✅ [PLAN-LIMITS] Plano determinado:', plan, priceId || '(sem price id)');
        return plan;
      }

      // Sem assinatura: trial do app (7 dias) → full access
      try {
        const trialInfo = await trialService.getTrialInfo(user.uid, user.email || '');
        if (trialInfo && !trialInfo.isExpired && trialInfo.timeRemaining > 0) {
          return 'trial';
        }
      } catch (trialErr) {
        console.warn('⚠️ [PLAN-LIMITS] Falha ao ler trial:', trialErr);
      }

      // Trial expirado / sem assinatura → starter (SubscriptionGuard bloqueia a UI)
      return 'starter';
    } catch (error) {
      console.error('❌ [PLAN-LIMITS] Erro ao determinar plano:', error);
      return 'starter';
    }
  }

  async getCurrentPlanLimits(): Promise<PlanLimits> {
    const currentPlan = await this.getCurrentPlan();
    return toServiceLimits(currentPlan);
  }

  async checkLimit(
    limitType: keyof PlanLimits,
    currentUsage: number,
    requestedAmount: number = 1
  ): Promise<LimitCheckResult> {
    try {
      const limits = await this.getCurrentPlanLimits();
      const limit = limits[limitType];

      if (limit === 'unlimited') {
        return { allowed: true, currentUsage, limit: 'unlimited', remaining: 'unlimited' };
      }

      if (typeof limit === 'boolean') {
        return {
          allowed: limit,
          currentUsage,
          limit: limit ? 'unlimited' : 0,
          remaining: limit ? 'unlimited' : 0,
          upgradeRequired: !limit,
          message: limit ? undefined : this.getLimitMessage(limitType, currentUsage, 0),
        };
      }

      if (typeof limit !== 'number') {
        return { allowed: true, currentUsage, limit: 'unlimited', remaining: 'unlimited' };
      }

      const totalAfterRequest = currentUsage + requestedAmount;
      const allowed = totalAfterRequest <= limit;
      const remaining = Math.max(0, limit - currentUsage);

      const result: LimitCheckResult = {
        allowed,
        currentUsage,
        limit,
        remaining: allowed ? remaining - requestedAmount : remaining,
        upgradeRequired: !allowed,
      };

      if (!allowed) {
        result.message = this.getLimitMessage(limitType, currentUsage, limit);
      }

      return result;
    } catch (error) {
      console.error('❌ [PLAN-LIMITS] Erro ao verificar limite:', error);
      // Em erro técnico: negar ações de cota (mais seguro que liberar)
      return {
        allowed: false,
        currentUsage,
        limit: 0,
        remaining: 0,
        upgradeRequired: true,
        message: 'Não foi possível verificar o limite do seu plano. Tente novamente.',
      };
    }
  }

  private getLimitMessage(
    limitType: keyof PlanLimits,
    _currentUsage: number,
    limit: number | 'unlimited'
  ): string {
    const messages: Partial<Record<keyof PlanLimits, string>> = {
      patients:
        typeof limit === 'number'
          ? `Limite de ${limit} pacientes do seu plano atingido. Faça upgrade para cadastrar mais.`
          : 'Limite de pacientes atingido.',
      professionals:
        typeof limit === 'number'
          ? `Limite de ${limit} profissionais do seu plano atingido. Faça upgrade para adicionar mais.`
          : 'Limite de profissionais atingido.',
      telemedicineSessions:
        typeof limit === 'number'
          ? `Limite de ${limit} consultas de telemedicina/mês atingido. Faça upgrade para continuar.`
          : 'Limite de telemedicina atingido.',
      storageGB:
        typeof limit === 'number'
          ? `Limite de ${limit}GB de armazenamento atingido. Faça upgrade para continuar.`
          : 'Limite de armazenamento atingido.',
      aiAnalyses: 'Análises de IA não disponíveis ou esgotadas no seu plano.',
      pharmacyIntegration: 'Farmácia não disponível no seu plano. Faça upgrade.',
      laboratoryIntegration: 'Laboratório não disponível no seu plano. Faça upgrade.',
      apiAccess: 'Acesso à API não disponível no seu plano.',
      hasAI: 'IA médica temporariamente indisponível no seu plano.',
      hasHomeCare: 'Home Care com GPS disponível no plano Enterprise.',
      hasTiss: 'Convênios/TISS disponível no plano Enterprise.',
      hasSngpc: 'SNGPC disponível no plano Enterprise.',
      hasInjectables: 'Injetáveis disponível a partir do plano Professional.',
      hasRecurringBilling: 'Cobrança recorrente disponível a partir do plano Professional.',
      hasAdvancedReports: 'Relatórios avançados disponíveis a partir do plano Professional.',
      hasFinancialModule: 'Módulo financeiro completo não disponível no seu plano.',
      managerDedicated: 'Gestor dedicado disponível no plano Enterprise.',
    };
    return messages[limitType] || 'Recurso não disponível no seu plano atual. Faça upgrade.';
  }

  async isFeatureAvailable(feature: string): Promise<boolean> {
    try {
      const tier = await this.getCurrentPlan();
      // Aceita chaves do catálogo e aliases usados no SubscriptionGuard
      const key = feature as PlanFeatureKey;
      if (featureAvailable(tier, key)) return true;

      // Aliases legados
      const aliasMap: Record<string, PlanFeatureKey> = {
        ai: 'hasAI',
        medicalAI: 'hasAI',
        homecare: 'hasHomeCare',
        homeCare: 'hasHomeCare',
        tiss: 'hasTiss',
        sngpc: 'hasSngpc',
        injectables: 'hasInjectables',
        pharmacy: 'pharmacyIntegration',
        laboratory: 'laboratoryIntegration',
        billing: 'hasRecurringBilling',
        recurringBilling: 'hasRecurringBilling',
        advancedReports: 'hasAdvancedReports',
        financial: 'hasFinancialModule',
      };
      const mapped = aliasMap[feature];
      if (mapped) return featureAvailable(tier, mapped);

      return false;
    } catch (error) {
      console.error('❌ [PLAN-LIMITS] Erro ao verificar recurso:', error);
      return false;
    }
  }

  async getPlanInfo(): Promise<{
    planName: string;
    limits: PlanLimits;
    isTrial: boolean;
    upgradeAvailable: boolean;
  }> {
    try {
      const currentPlan = await this.getCurrentPlan();
      const limits = toServiceLimits(currentPlan);
      const catalog = getPlanLimits(currentPlan);
      const subscriptionStatus = await subscriptionStatusService.checkSubscriptionStatus(
        this.auth.currentUser?.uid || ''
      );

      return {
        planName: catalog.planName,
        limits,
        isTrial: currentPlan === 'trial' || subscriptionStatus.isTrialing,
        upgradeAvailable: currentPlan !== 'enterprise',
      };
    } catch (error) {
      console.error('❌ [PLAN-LIMITS] Erro ao obter informações do plano:', error);
      return {
        planName: 'Trial',
        limits: toServiceLimits('trial'),
        isTrial: true,
        upgradeAvailable: true,
      };
    }
  }

  async getUpgradeRecommendations(currentUsage: Partial<CurrentUsage>): Promise<{
    recommendedPlan: string;
    reasons: string[];
    benefits: string[];
  }> {
    try {
      const currentPlan = await this.getCurrentPlan();
      const limits = toServiceLimits(currentPlan);
      const reasons: string[] = [];

      Object.entries(currentUsage).forEach(([key, value]) => {
        const limitKey = key as keyof PlanLimits;
        const limit = limits[limitKey];
        if (typeof limit === 'number' && typeof value === 'number') {
          const usagePercentage = (value / limit) * 100;
          if (usagePercentage >= 80) {
            reasons.push(`${key}: ${usagePercentage.toFixed(0)}% do limite usado`);
          }
        }
      });

      let recommendedPlan = 'professional';
      const benefits: string[] = [];

      if (currentPlan === 'trial' || currentPlan === 'starter' || currentPlan === 'solo') {
        recommendedPlan = 'professional';
        benefits.push('Pacientes ilimitados', 'Até 5 profissionais', 'IA médica', 'Telemedicina avançada');
      } else if (currentPlan === 'professional') {
        recommendedPlan = 'plus';
        benefits.push('Até 15 profissionais', 'Home Care GPS', 'TISS/SNGPC', 'Suporte 24/7');
      } else if (currentPlan === 'plus') {
        recommendedPlan = 'enterprise';
        benefits.push('Profissionais ilimitados', 'Gestor dedicado', 'Suporte 24/7');
      }

      return { recommendedPlan, reasons, benefits };
    } catch {
      return {
        recommendedPlan: 'professional',
        reasons: [],
        benefits: ['Recursos expandidos'],
      };
    }
  }
}

export const planLimitsService = new PlanLimitsService();
export default planLimitsService;
